import { ArrowRight, MessageCircle } from "lucide-react";
import { ARVI, waLink, DEFAULT_WA_MESSAGE } from "../../lib/arvi";
import { IMAGES } from "../../lib/images";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-ivory pt-28 md:pt-36 pb-20 md:pb-28"
    >
      {/* Soft parchment vignette */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at top, hsl(var(--parchment)) 0%, hsl(var(--ivory)) 65%)",
        }}
      />
      <div className="grain absolute inset-0 pointer-events-none opacity-60" aria-hidden="true" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Couture-house masthead */}
        <div className="text-center reveal">
          <p
            data-testid="hero-overline"
            className="font-display italic text-maroon/75 text-lg md:text-2xl mb-6 md:mb-8"
          >
            A Mother's Art. <span className="text-gold not-italic">·</span> A Son's Promise.
          </p>

          <div className="flex items-center justify-center gap-4 md:gap-6">
            <span aria-hidden="true" className="hidden sm:block h-px w-16 md:w-28 bg-gold" />
            <h1
              data-testid="hero-headline"
              className="font-display text-maroon leading-none"
              style={{
                fontSize: "clamp(4.5rem, 16vw, 14rem)",
                letterSpacing: "0.18em",
                fontWeight: 500,
              }}
            >
              ARVI
            </h1>
            <span aria-hidden="true" className="hidden sm:block h-px w-16 md:w-28 bg-gold" />
          </div>

          <p
            className="mt-2 md:mt-3 font-display italic text-maroon/80 tracking-[0.05em]"
            style={{ fontSize: "clamp(1.5rem, 4vw, 3.25rem)" }}
          >
            Heirlooms
          </p>

          <div
            aria-hidden="true"
            className="mt-10 md:mt-14 mx-auto flex items-center justify-center gap-3 text-gold"
          >
            <span className="h-px w-10 bg-gold/60" />
            <span className="text-xs">✦</span>
            <span className="h-px w-24 md:w-40 bg-gold/60" />
            <span className="text-xs">✦</span>
            <span className="h-px w-10 bg-gold/60" />
          </div>
        </div>

        {/* Editorial split below the masthead */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          {/* Left: editorial copy */}
          <div className="md:col-span-6 md:col-start-1 reveal order-2 md:order-1">
            <p className="text-overline text-gold mb-5">No. 01 — The House</p>
            <h2 className="font-display text-maroon leading-[1.05] tracking-tight text-[clamp(2rem,4vw,3.25rem)]">
              Hand-painted luxury,
              <br />
              <span className="italic">woven in legacy.</span>
            </h2>
            <p className="mt-7 max-w-md text-maroon/75 text-[15px] md:text-base leading-[1.85]">
              Forty years of devotion. One brush. Countless stories. Every ARVI
              saree is painted by hand — a wearable heirloom created slowly,
              deliberately, and only for you.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={waLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="btn-cta-base btn-wa"
              >
                <MessageCircle className="h-4 w-4" />
                Enquire on WhatsApp
              </a>
              <a
                href="#design"
                data-testid="hero-collections-cta"
                className="btn-cta-base btn-ghost-light"
              >
                Design Your Saree
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            <div className="mt-12 flex items-center gap-8 md:gap-10">
              <div>
                <p className="font-display text-3xl md:text-4xl text-maroon">40+</p>
                <p className="text-overline text-maroon/55 mt-1">Years</p>
              </div>
              <div className="h-10 w-px bg-gold/60" />
              <div>
                <p className="font-display text-3xl md:text-4xl text-maroon">100%</p>
                <p className="text-overline text-maroon/55 mt-1">Hand-Painted</p>
              </div>
              <div className="h-10 w-px bg-gold/60" />
              <div>
                <p className="font-display text-3xl md:text-4xl text-maroon">1/1</p>
                <p className="text-overline text-maroon/55 mt-1">Of One</p>
              </div>
            </div>
          </div>

          {/* Right: framed portrait */}
          <div className="md:col-span-5 md:col-start-8 reveal order-1 md:order-2">
            <figure className="relative max-w-md mx-auto md:mx-0">
              <span
                aria-hidden="true"
                className="absolute -top-5 -left-5 md:-top-7 md:-left-7 w-full h-full border border-gold/70"
              />
              <span
                aria-hidden="true"
                className="absolute -top-5 -left-5 md:-top-7 md:-left-7 w-12 h-px bg-gold"
                style={{ transform: "translate(0, 0)" }}
              />
              <div className="relative overflow-hidden bg-maroon-deep">
                <img
                  src={IMAGES.hero}
                  alt="A bride in an ARVI hand-painted saree"
                  className="w-full h-[480px] md:h-[640px] object-cover"
                  style={{ objectPosition: "center 25%" }}
                  loading="eager"
                />
              </div>
              <figcaption className="mt-5 flex items-center justify-between text-overline text-maroon/55">
                <span>Plate I — The Drape</span>
                <span className="font-display italic text-gold">i</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
